import React, { useEffect, useMemo, useState } from "react";
import { useApiCall } from "../custom-hooks/api-call";

/* =========================================================================
   Legendesk Sales — Performance dashboard (original design)
   Two screens: Agents (original) + Teams (rolled-up pods).
   Shared data engine so totals tie out across both views.
   ========================================================================= */

// ---- palette -------------------------------------------------------------
const C = {
  linen: "#F7ECDF", linenDeep: "#EFE1CF", ink: "#0A0C12", ink70: "#3A3D47",
  ink50: "#6B6E78", ink30: "#9A9CA4", card: "#FFFFFF", line: "#E7DAC6",
  lineSoft: "#F0E6D6", indigo: "#4F6EF7", indigoSoft: "#EAEEFF",
  green: "#1F9D6B", greenSoft: "#E4F5EC", amber: "#C9871A", amberSoft: "#FBEFD6",
  red: "#D0503C", redSoft: "#FBE6E0",
  gray: "#6B7280",
  graySoft: "#F3F4F6",
};
const FONT_DISP = "'Sora', system-ui, sans-serif";
const FONT_BODY = "'DM Sans', system-ui, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

// ---- rng -----------------------------------------------------------------
function mulberry32(a){return function(){a|=0;a=(a+0x6d2b79f5)|0;let t=Math.imul(a^(a>>>15),1|a);t=(t+Math.imul(t^(t>>>7),61|t))^t;return((t^(t>>>14))>>>0)/4294967296;};}
function hash(s){let h=2166136261;for(let i=0;i<s.length;i++){h^=s.charCodeAt(i);h=Math.imul(h,16777619);}return h>>>0;}
function gauss(r){return (r()+r()+r())/3;}

const FIRST=["Ayesha","Bilal","Zara","Hamza","Sana","Usman","Mariam","Faisal","Noor","Ahmed","Hira","Talha","Iqra","Danish","Fatima","Saad","Amna","Rehan","Kiran","Owais","Mehak","Zain","Rida","Adeel","Anaya","Kashif","Laiba","Waleed","Sadia","Umar","Aiman","Junaid","Sania","Haris","Areeba","Fahad","Nimra","Shahzad","Warda","Bilawal","Emma","Liam","Sophia","Noah","Olivia","Ethan","Mia","Lucas","Ella","Mason"];
const LAST=["Khan","Malik","Sheikh","Raza","Qureshi","Butt","Ansari","Hashmi","Siddiqui","Farooq","Chaudhry","Baig","Awan","Mirza","Rana","Shah","Gill","Dar","Jamil","Nawaz","Rivera","Chen","Patel","Nguyen","Brooks","Silva"];
const TEAMS=["Apex","Titan","Falcon","Orbit","Vanguard"];

function buildAgents(count=100){
  const rng=mulberry32(20260703);const out=[];const used=new Set();
  for(let i=0;i<count;i++){
    let name;do{name=FIRST[Math.floor(rng()*FIRST.length)]+" "+LAST[Math.floor(rng()*LAST.length)];}while(used.has(name));used.add(name);
    const skill=0.55+gauss(rng)*0.85;const vol=0.05+rng()*0.22;
    const baseTarget=9000+Math.round(rng()*17000);
    const refundBase=rng()<0.16?0.14+rng()*0.16:0.02+rng()*0.09;
    const history=[];let drift=(rng()-0.5)*0.06;
    for(let p=0;p<6;p++)history.push(Math.max(0.15,skill+drift*(p-2.5)+(gauss(rng)-0.5)*vol*2));
    out.push({id:"AG"+String(i+1).padStart(3,"0"),name,team:TEAMS[i%TEAMS.length],skill,vol,baseTarget,refundBase,history});
  }
  return out;
}
const AGENTS=buildAgents(100);

const PERIODS=[
  {key:"today",label:"Today",factor:1/22},{key:"yesterday",label:"Yesterday",factor:1/22},
  {key:"this_week",label:"This week",factor:1/4.3},{key:"this_month",label:"This month",factor:1},
  {key:"last_30",label:"Last 30 days",factor:1},{key:"last_month",label:"Last month",factor:0.96},
  {key:"rolling_q",label:"Rolling quarter",factor:3},{key:"this_q",label:"This quarter",factor:2.1},
  {key:"last_q",label:"Last quarter",factor:3},{key:"this_year",label:"This year",factor:6.4},
  {key:"all_time",label:"All time",factor:27},
  {key:"custom",label:"Custom"},
];

function computeRow(agent,period){
  const rng=mulberry32(hash(agent.id+"|"+period.key));
  const attain=Math.max(0.12,agent.skill+(gauss(rng)-0.5)*agent.vol*2.2);
  const target=Math.round(agent.baseTarget*period.factor);
  const gross=Math.round(target*attain);
  const refundRatio=Math.max(0.005,agent.refundBase+(gauss(rng)-0.5)*0.05);
  const refunds=Math.round(gross*refundRatio);const net=gross-refunds;
  return {...agent,target,gross,net,refunds,refundRatio,progress:target>0?net/target:0};
}
function statusOf(p){if(p>=1)return"on_target";if(p>=0.9)return"on_track";if(p>=0.7)return"at_risk";return"behind";}
const STATUS_META={
  on_target:{label:"On target",c:C.green,bg:C.greenSoft},
  on_track:{label:"On track",c:C.indigo,bg:C.indigoSoft},
  at_risk:{label:"At risk",c:C.amber,bg:C.amberSoft},
  behind:{label:"Behind",c:C.red,bg:C.redSoft},
  unassigned:{ label: "Unassigned", c: C.gray, bg: C.graySoft}
};
function trendOf(h){const avg=h.reduce((a,b)=>a+b,0)/h.length;const std=Math.sqrt(h.reduce((a,b)=>a+(b-avg)**2,0)/h.length);const s=h[h.length-1]-h[0];if(std<0.12&&avg>=0.95)return"consistent";if(s>0.18)return"improving";if(s<-0.18)return"declining";if(std>=0.22)return"volatile";return"steady";}
const TREND_META={
  consistent:{label:"Consistent",c:C.green},
  improving:{label:"Improving",c:C.indigo},
  steady:{label:"Steady",c:C.ink50},
  volatile:{label:"Volatile",c:C.amber},
  declining:{label:"Declining",c:C.red}
};

function money(n){const a=Math.abs(n);const s=n<0?"-":"";if(a>=1e6)return s+"$"+(a/1e6).toFixed(a>=1e7?1:2)+"M";if(a>=1e3)return s+"$"+(a/1e3).toFixed(a>=1e4?0:1)+"K";return s+"$"+Math.round(a);}
const pct=(x)=>Math.round(x*100)+"%";
const initials=(n)=>{const p=n.split(" ");return(p[0][0]+(p[1]?p[1][0]:"")).toUpperCase();};

// ---- shared components ---------------------------------------------------
function Sparkline({data,color,w=60,h=20}){
  const pad=2;const min=Math.min(...data),max=Math.max(...data),span=max-min||1;
  const pts=data.map((v,i)=>[pad+(i/(data.length-1))*(w-pad*2),h-pad-((v-min)/span)*(h-pad*2)]);
  const d=pts.map((p,i)=>(i?"L":"M")+p[0].toFixed(1)+" "+p[1].toFixed(1)).join(" ");
  return(<svg width={w} height={h} style={{display:"block"}}><path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><circle cx={pts.length ? pts[pts.length-1][0] : 0} cy={pts.length ? pts[pts.length-1][1] : 0} r="2" fill={color}/></svg>);
}
function ProgressBar({value,color}){
  const capped=Math.min(value,1.25);
  return(
    <div style={{display:"flex",alignItems:"center",gap:8}}>
      <div style={{position:"relative",flex:1,height:7,background:C.lineSoft,borderRadius:99,overflow:"hidden"}}>
        <div style={{position:"absolute",left:`${(1/1.25)*100}%`,top:0,bottom:0,width:1,background:C.ink30}}/>
        <div style={{height:"100%",width:`${(capped/1.25)*100}%`,background:color,borderRadius:99,transition:"width .35s ease"}}/>
      </div>
      <span style={{fontFamily:FONT_MONO,fontSize:12,color:C.ink,minWidth:40,textAlign:"right"}}>{pct(value)}</span>
    </div>
  );
}
function Pill({label,color,bg}){
  return(<span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 9px",borderRadius:99,background:bg,color,fontSize:11.5,fontWeight:600}}><span style={{width:6,height:6,borderRadius:99,background:color}}/>{label}</span>);
}
function SummaryCard({label,value,sub,accent,bar}){
  return(
    <div style={{background:C.card,border:`1px solid ${C.line}`,borderRadius:16,padding:"16px 18px",flex:1,minWidth:0}}>
      <div style={{fontSize:12,color:C.ink50,fontWeight:600,textTransform:"uppercase",letterSpacing:0.6}}>{label}</div>
      <div style={{fontFamily:FONT_DISP,fontSize:26,fontWeight:700,color:accent||C.ink,marginTop:6,lineHeight:1.1}}>{value}</div>
      {bar!=null&&<div style={{marginTop:10}}><ProgressBar value={bar} color={C.indigo}/></div>}
      {sub&&<div style={{fontSize:12.5,color:C.ink50,marginTop:bar!=null?8:6}}>{sub}</div>}
    </div>
  );
}
function Th({label,k,sortKey,sortDir,onSort,align="left",w}){
  const active=sortKey===k;
  return(
    <th onClick={k?()=>onSort(k):undefined} style={{textAlign:align,padding:"0 14px",height:38,width:w,fontSize:11.5,fontWeight:600,color:active?C.ink:C.ink50,textTransform:"uppercase",letterSpacing:0.5,cursor:k?"pointer":"default",userSelect:"none",whiteSpace:"nowrap",position:"sticky",top:0,background:C.linenDeep,zIndex:2}}>
      <span style={{display:"inline-flex",alignItems:"center",gap:4,justifyContent:align==="right"?"flex-end":"flex-start"}}>{label}{k&&<span style={{fontSize:9,color:active?C.indigo:C.ink30}}>{active?(sortDir==="asc"?"▲":"▼"):"⇅"}</span>}</span>
    </th>
  );
}

// ==========================================================================
export default function Dashboard(){
  const apiCall = useApiCall();
  const [view,setView]=useState("teams"); // "agents" | "teams"
  const [periodKey,setPeriodKey]=useState("this_month");
  const [pickerOpen,setPickerOpen]=useState(false);
  const period=PERIODS.find(p=>p.key===periodKey);

  // ---- custom date range state ----
  const [showCustomPicker,setShowCustomPicker]=useState(false);
  const [draftStart,setDraftStart]=useState("");
  const [draftEnd,setDraftEnd]=useState("");
  const [customStart,setCustomStart]=useState("");
  const [customEnd,setCustomEnd]=useState("");

  function formatDateLabel(d){return new Date(d+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});}
  const customRangeLabel=customStart&&customEnd?`${formatDateLabel(customStart)} – ${formatDateLabel(customEnd)}`:"Custom";
  const periodLabel=periodKey==="custom"?customRangeLabel:period.label;

  function openPicker(){
    setPickerOpen(o=>{
      const next=!o;
      if(next){setShowCustomPicker(periodKey==="custom");setDraftStart(customStart);setDraftEnd(customEnd);}
      return next;
    });
  }
  function selectPeriod(key){setPeriodKey(key);setPickerOpen(false);setShowCustomPicker(false);}
  function openCustomPicker(){setShowCustomPicker(true);setDraftStart(customStart);setDraftEnd(customEnd);}
  function applyCustomRange(){
    if(!draftStart||!draftEnd||draftStart>draftEnd)return;
    setCustomStart(draftStart);setCustomEnd(draftEnd);
    setPeriodKey("custom");setShowCustomPicker(false);setPickerOpen(false);
  }
  function cancelCustomRange(){setShowCustomPicker(false);}

  // agent-view state
  const [search,setSearch]=useState("");
  const [teamFilter,setTeamFilter]=useState("all");
  const [statusSet,setStatusSet]=useState(new Set());
  const [refundOnly,setRefundOnly]=useState(false);
  const [aSortKey,setASortKey]=useState("progress");
  const [aSortDir,setASortDir]=useState("desc");

  // team-view state
  const [tSortKey,setTSortKey]=useState("progress");
  const [tSortDir,setTSortDir]=useState("desc");
  const [expanded,setExpanded]=useState(new Set());

  // ---- per-agent rows for the period ----
  // const rows=useMemo(()=>AGENTS.map(a=>{const r=computeRow(a,period);r.status=statusOf(r.progress);r.trend=trendOf(a.history);return r;}),[periodKey]);
  // console.log("rows", rows)

  const [rows, setRows] = useState([])

  const getAgents = async() => {
    if(periodKey==="custom"&&(!customStart||!customEnd))return;
    try {
      const rangeQs=periodKey==="custom"?`&from=${customStart}&to=${customEnd}`:"";
      const apiRes = await apiCall({method: "get", url: `agents?period=${periodKey}${rangeQs}`})
      console.log("getAgents", apiRes);
      setRows(apiRes.data)
    } catch (error) {
      console.log("Err", error)
    }
  }

  useEffect(() => {
    getAgents();
  }, [periodKey,customStart,customEnd])
  

  // ---- teams rollup ----
  // const teams=useMemo(()=>{
  //   const map={};
  //   TEAMS.forEach(t=>map[t]={name:t,agents:[],target:0,gross:0,net:0,refunds:0,onTarget:0,histSum:[0,0,0,0,0,0]});
  //   rows.forEach(r=>{
  //     const t=map[r.team];t.agents.push(r);
  //     t.target+=r.target;t.gross+=r.gross;t.net+=r.net;t.refunds+=r.refunds;
  //     if(r.status==="on_target")t.onTarget++;
  //     r.history.forEach((v,i)=>t.histSum[i]+=v);
  //   });
  //   return Object.values(map).map(t=>{
  //     const n=t.agents.length;
  //     const history=t.histSum.map(v=>v/n);
  //     const progress=t.target?t.net/t.target:0;
  //     const refundRatio=t.gross?t.refunds/t.gross:0;
  //     return {...t,count:n,history,progress,refundRatio,status:statusOf(progress),trend:trendOf(history)};
  //   });
  // },[rows]);

  // console.log("teams", teams)

  

  const [teams, setTeams] = useState([]);

  const getTeams = async() => {
    if(periodKey==="custom"&&(!customStart||!customEnd))return;
    try {
      const rangeQs=periodKey==="custom"?`&from=${customStart}&to=${customEnd}`:"";
      const apiRes = await apiCall({method: "get", url: `teams-with-agents?period=${periodKey}${rangeQs}`})
      // console.log("apiRes", apiRes);
      setTeams(apiRes.data)
    } catch (error) {
      console.log("Err", error)
    }
  }

  useEffect(() => {
    getTeams();
  }, [periodKey,customStart,customEnd])
  

  // ---- agent filtering/sort ----
  const agentsFiltered=useMemo(()=>{
    let list=rows;
    if(search.trim()){const q=search.toLowerCase();list=list.filter(r=>r.name.toLowerCase().includes(q)||r.id.toLowerCase().includes(q));}
    if(teamFilter!=="all")list=list.filter(r=>r.team===teamFilter);
    if(statusSet.size)list=list.filter(r=>statusSet.has(r.status));
    if(refundOnly)list=list.filter(r=>r.refundRatio>=0.15);
    const dir=aSortDir==="asc"?1:-1;
    return [...list].sort((a,b)=>aSortKey==="name"?a.name.localeCompare(b.name)*dir:(a[aSortKey]-b[aSortKey])*dir);
  },[rows,search,teamFilter,statusSet,refundOnly,aSortKey,aSortDir]);

  const aSum=useMemo(()=>{const s={target:0,gross:0,net:0,refunds:0,onTarget:0};agentsFiltered.forEach(r=>{s.target+=r.target;s.gross+=r.gross;s.net+=r.net;s.refunds+=r.refunds;if(r.status==="on_target")s.onTarget++;});s.progress=s.target?s.net/s.target:0;s.refundRatio=s.gross?s.refunds/s.gross:0;return s;},[agentsFiltered]);

  // ---- team sort + summary ----
  const teamsSorted=useMemo(()=>{const dir=tSortDir==="asc"?1:-1;return [...teams].sort((a,b)=>tSortKey==="name"?a.name.localeCompare(b.name)*dir:(a[tSortKey]-b[tSortKey])*dir);},[teams,tSortKey,tSortDir]);
  const tSum=useMemo(()=>{const s={target:0,gross:0,net:0,refunds:0,agents:0,onTargetTeams:0};teams.forEach(t=>{s.target+=t.target;s.gross+=t.gross;s.net+=t.net;s.refunds+=t.refunds;s.agents+=t.count;if(t.status==="on_target")s.onTargetTeams++;});s.progress=s.target?s.net/s.target:0;s.refundRatio=s.gross?s.refunds/s.gross:0;return s;},[teams]);

  function aSort(k){if(aSortKey===k)setASortDir(d=>d==="asc"?"desc":"asc");else{setASortKey(k);setASortDir(k==="name"?"asc":"desc");}}
  function tSort(k){if(tSortKey===k)setTSortDir(d=>d==="asc"?"desc":"asc");else{setTSortKey(k);setTSortDir(k==="name"?"asc":"desc");}}
  function toggleStatus(k){setStatusSet(prev=>{const n=new Set(prev);n.has(k)?n.delete(k):n.add(k);return n;});}
  function toggleExpand(name){setExpanded(prev=>{const n=new Set(prev);n.has(name)?n.delete(name):n.add(name);return n;});}

  const selBar={padding:"9px 12px",borderRadius:10,border:`1px solid ${C.line}`,background:C.card,fontFamily:FONT_BODY,fontSize:13.5,color:C.ink,outline:"none",cursor:"pointer"};

  console.log("periodLabel", periodLabel);

  return(
    <div style={{minHeight:"100vh",background:C.linen,fontFamily:FONT_BODY,color:C.ink,padding:"24px 28px 40px"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box}table{border-collapse:collapse}
        ::-webkit-scrollbar{height:9px;width:9px}::-webkit-scrollbar-thumb{background:#D8C7AF;border-radius:9px}
        input::placeholder{color:${C.ink30}}
      `}</style>

      <div style={{maxWidth:1240,margin:"0 auto"}}>
        {/* header */}
        <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",flexWrap:"wrap",gap:14,marginBottom:18}}>
          <div>
            <div style={{fontSize:12,fontWeight:600,color:C.indigo,textTransform:"uppercase",letterSpacing:1.4}}>Legendesk · Sales</div>
            <h1 style={{fontFamily:FONT_DISP,fontSize:30,fontWeight:700,margin:"4px 0 0",letterSpacing:-0.5}}>{view==="agents"?"Agent Performance":"Team Performance"}</h1>
          </div>
          <div style={{position:"relative"}}>
            <button onClick={openPicker} style={{...selBar,display:"inline-flex",alignItems:"center",gap:10,fontWeight:600}}>
              <span style={{width:7,height:7,borderRadius:99,background:C.indigo}}/>{periodLabel}<span style={{color:C.ink30,fontSize:11}}>▼</span>
            </button>
            {pickerOpen&&(
              <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:C.card,border:`1px solid ${C.line}`,borderRadius:12,padding:6,boxShadow:"0 12px 30px rgba(10,12,18,.12)",zIndex:20,width:220}}>
                {PERIODS.filter(p=>p.key!=="custom").map(p=>(<button key={p.key} onClick={()=>selectPeriod(p.key)} style={{display:"block",width:"100%",textAlign:"left",padding:"8px 10px",borderRadius:8,border:"none",cursor:"pointer",fontFamily:FONT_BODY,fontSize:13.5,fontWeight:p.key===periodKey?600:400,color:p.key===periodKey?C.indigo:C.ink70,background:p.key===periodKey?C.indigoSoft:"transparent"}}>{p.label}</button>))}
                <button onClick={openCustomPicker} style={{display:"block",width:"100%",textAlign:"left",padding:"8px 10px",borderRadius:8,border:"none",cursor:"pointer",fontFamily:FONT_BODY,fontSize:13.5,fontWeight:periodKey==="custom"?600:400,color:periodKey==="custom"?C.indigo:C.ink70,background:periodKey==="custom"?C.indigoSoft:"transparent"}}>
                  {periodKey==="custom"?customRangeLabel:"Custom…"}
                </button>
                {showCustomPicker&&(
                  <div style={{padding:10,borderTop:`1px solid ${C.lineSoft}`,marginTop:4,display:"flex",flexDirection:"column",gap:8}}>
                    <label style={{fontSize:11.5,color:C.ink50,fontWeight:600}}>Start date
                      <input type="date" value={draftStart} max={draftEnd||undefined} onChange={e=>setDraftStart(e.target.value)} style={{display:"block",width:"100%",marginTop:4,padding:"6px 8px",borderRadius:8,border:`1px solid ${C.line}`,fontFamily:FONT_BODY,fontSize:13}}/>
                    </label>
                    <label style={{fontSize:11.5,color:C.ink50,fontWeight:600}}>End date
                      <input type="date" value={draftEnd} min={draftStart||undefined} onChange={e=>setDraftEnd(e.target.value)} style={{display:"block",width:"100%",marginTop:4,padding:"6px 8px",borderRadius:8,border:`1px solid ${C.line}`,fontFamily:FONT_BODY,fontSize:13}}/>
                    </label>
                    <div style={{display:"flex",gap:6,marginTop:2}}>
                      <button onClick={applyCustomRange} disabled={!draftStart||!draftEnd||draftStart>draftEnd} style={{flex:1,padding:"7px 10px",borderRadius:8,border:"none",cursor:(!draftStart||!draftEnd||draftStart>draftEnd)?"not-allowed":"pointer",background:C.indigo,color:"#fff",fontSize:12.5,fontWeight:600,opacity:(!draftStart||!draftEnd||draftStart>draftEnd)?0.5:1}}>Apply</button>
                      <button onClick={cancelCustomRange} style={{flex:1,padding:"7px 10px",borderRadius:8,border:`1px solid ${C.line}`,cursor:"pointer",background:C.card,color:C.ink70,fontSize:12.5,fontWeight:600}}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* view toggle */}
        <div style={{display:"inline-flex",background:C.linenDeep,borderRadius:12,padding:4,gap:4,marginBottom:18}}>
          {[["agents","Agents"],["teams","Teams"]].map(([k,lbl])=>(
            <button key={k} onClick={()=>setView(k)} style={{padding:"8px 18px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:FONT_BODY,fontSize:13.5,fontWeight:600,color:view===k?C.ink:C.ink50,background:view===k?C.card:"transparent",boxShadow:view===k?"0 1px 3px rgba(10,12,18,.10)":"none"}}>{lbl}</button>
          ))}
        </div>

        {view==="agents"?(
          <AgentsView {...{agentsFiltered,rows,aSum,period,periodLabel,search,setSearch,teamFilter,setTeamFilter,statusSet,toggleStatus,refundOnly,setRefundOnly,setStatusSet,aSortKey,aSortDir,aSort,selBar}}/>
        ):(
          <TeamsView {...{teamsSorted,tSum,period,periodLabel,tSortKey,tSortDir,tSort,expanded,toggleExpand}}/>
        )}
      </div>
    </div>
  );
}

// ---- AGENTS VIEW (original) ----------------------------------------------
function AgentsView({agentsFiltered,rows,aSum,period,search,periodLabel,setSearch,teamFilter,setTeamFilter,statusSet,toggleStatus,refundOnly,setRefundOnly,setStatusSet,aSortKey,aSortDir,aSort,selBar}){

  console.log("agentsFiltered", agentsFiltered)

  return(<>
    <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:14}}>
      <SummaryCard label="Active agents" value={agentsFiltered.length} sub={`${aSum.onTarget} on target · ${agentsFiltered.length-aSum.onTarget} below`}/>
      <SummaryCard label="Total target" value={money(aSum.target)} sub={periodLabel}/>
      <SummaryCard label="Net achieved" value={money(aSum.net)} bar={aSum.progress}/>
      <SummaryCard label="Gross achieved" value={money(aSum.gross)} sub={`Net after refunds: ${money(aSum.net)}`}/>
      <SummaryCard label="Lost revenue" value={money(aSum.refunds)} accent={C.red} sub={`Refund ratio ${pct(aSum.refundRatio)}`}/>
    </div>

    <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search agent or ID…" style={{...selBar,cursor:"text",minWidth:200,flex:"0 1 240px"}}/>
      <select value={teamFilter} onChange={e=>setTeamFilter(e.target.value)} style={selBar}><option value="all">All teams</option>{TEAMS.map(t=><option key={t} value={t}>Pod {t}</option>)}</select>
      <div style={{display:"flex",gap:6}}>
        {Object.entries(STATUS_META).map(([k,m])=>{const on=statusSet.has(k);return(
          <button key={k} onClick={()=>toggleStatus(k)} style={{padding:"8px 11px",borderRadius:99,border:`1px solid ${on?m.c:C.line}`,background:on?m.bg:C.card,color:on?m.c:C.ink50,fontSize:12.5,fontWeight:600,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}><span style={{width:6,height:6,borderRadius:99,background:m.c}}/>{m.label}</button>
        );})}
      </div>
      <button onClick={()=>setRefundOnly(v=>!v)} style={{padding:"8px 12px",borderRadius:99,border:`1px solid ${refundOnly?C.red:C.line}`,background:refundOnly?C.redSoft:C.card,color:refundOnly?C.red:C.ink50,fontSize:12.5,fontWeight:600,cursor:"pointer"}}>High refunds only</button>
      {(statusSet.size>0||refundOnly||teamFilter!=="all"||search)&&(
        <button onClick={()=>{setStatusSet(new Set());setRefundOnly(false);setTeamFilter("all");setSearch("");}} style={{padding:"8px 12px",borderRadius:99,border:"none",background:"transparent",color:C.ink50,fontSize:12.5,fontWeight:600,cursor:"pointer",textDecoration:"underline"}}>Clear</button>
      )}
    </div>

    <div style={{background:C.card,border:`1px solid ${C.line}`,borderRadius:16,overflow:"hidden"}}>
      <div style={{maxHeight:560,overflow:"auto"}}>
        <table style={{width:"100%",minWidth:940}}>
          <thead><tr>
            <Th label="Agent" k="name" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort}/>
            <Th label="Target" k="target" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} align="right"/>
            <Th label="Gross" k="gross" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} align="right"/>
            <Th label="Net" k="net" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} align="right"/>
            <Th label="Lost" k="refunds" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} align="right"/>
            <Th label="Refund %" k="refundRatio" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} align="right"/>
            <Th label="Progress" k="progress" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} w={190}/>
            <Th label="Trend" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort}/>
            <Th label="Status" k="status" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort}/>
          </tr></thead>
          <tbody>
            {agentsFiltered.map((r,i)=>{
              const sm=STATUS_META[r.status] || { label: "Unassigned", c: C.gray, bg: C.graySoft};
              const tm=TREND_META[r.trend] || {label:"--",c:C.gray};
              const bad=r.refundRatio>=0.15;
              console.log("tm", tm)
              return(
              <tr key={r.id} style={{borderTop:`1px solid ${C.lineSoft}`,background:i%2?"#FffdF9":C.card}}>
                <td style={{padding:"10px 14px"}}><div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:32,height:32,borderRadius:10,background:sm.bg,color:sm.c,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:12,flexShrink:0}}>{initials(r.name)}</div>
                  <div style={{minWidth:0}}><div style={{fontWeight:600,fontSize:13.5,whiteSpace:"nowrap"}}>{r.name}</div>
                  {/* <div style={{fontSize:11.5,color:C.ink50}}>{r.id} · Pod {r.team}</div> */}
                  </div>
                </div></td>
                <td style={{padding:"10px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13,color:C.ink70}}>{money(r.target)}</td>
                <td style={{padding:"10px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13}}>{money(r.gross)}</td>
                <td style={{padding:"10px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13,fontWeight:500}}>{money(r.net)}</td>
                <td style={{padding:"10px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13,color:C.red}}>{money(r.refunds)}</td>
                <td style={{padding:"10px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13,color:bad?C.red:C.ink70,fontWeight:bad?600:400}}>{pct(r.refundRatio)}</td>
                <td style={{padding:"10px 14px",width:190}}><ProgressBar value={r.progress} color={sm.c}/></td>
                <td style={{padding:"10px 14px"}}><div style={{display:"flex",alignItems:"center",gap:8}}><Sparkline data={r.history} color={tm.c}/><span style={{fontSize:11.5,fontWeight:600,color:tm.c}}>{tm.label}</span></div></td>
                <td style={{padding:"10px 14px"}}><Pill label={sm.label} color={sm.c} bg={sm.bg}/></td>
              </tr>
            );})}
            {agentsFiltered.length===0&&<tr><td colSpan={9} style={{padding:"48px 14px",textAlign:"center",color:C.ink50}}>No agents match these filters. Clear a filter to see more.</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{padding:"10px 16px",borderTop:`1px solid ${C.lineSoft}`,fontSize:12,color:C.ink50,display:"flex",justifyContent:"space-between"}}>
        <span>Showing {agentsFiltered.length} of {rows.length} agents</span>
        <span>Progress measured on net vs target · {period.label}</span>
      </div>
    </div>
  </>);
}

// ---- TEAMS VIEW ----------------------------------------------------------
function TeamsView({teamsSorted,tSum,period,tSortKey,periodLabel,tSortDir,tSort,expanded,toggleExpand}){
  return(<>
    <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:14}}>
      <SummaryCard label="Teams" value={teamsSorted.length} sub={`${tSum.agents} agents · ${tSum.onTargetTeams} pods on target`}/>
      <SummaryCard label="Total target" value={money(tSum.target)} sub={periodLabel}/>
      <SummaryCard label="Net achieved" value={money(tSum.net)} bar={tSum.progress}/>
      <SummaryCard label="Gross achieved" value={money(tSum.gross)} sub={`Net after refunds: ${money(tSum.net)}`}/>
      <SummaryCard label="Lost revenue" value={money(tSum.refunds)} accent={C.red} sub={`Refund ratio ${pct(tSum.refundRatio)}`}/>
    </div>

    <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
      <span style={{fontSize:12.5,color:C.ink50}}>Tap a pod to see its agents. Sort by any column.</span>
    </div>

    <div style={{background:C.card,border:`1px solid ${C.line}`,borderRadius:16,overflow:"hidden"}}>
      <div style={{overflow:"auto"}}>
        <table style={{width:"100%",minWidth:960}}>
          <thead><tr>
            <Th label="Team" k="name" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort}/>
            <Th label="Agents" k="count" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right"/>
            <Th label="Target" k="target" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right"/>
            <Th label="Gross" k="gross" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right"/>
            <Th label="Net" k="net" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right"/>
            <Th label="Lost" k="refunds" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right"/>
            <Th label="Refund %" k="refundRatio" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right"/>
            <Th label="Progress" k="progress" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} w={190}/>
            <Th label="Trend" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort}/>
            <Th label="Status" k="status" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort}/>
          </tr></thead>
          <tbody>
            {teamsSorted.map((t,i)=>{
              const sm=STATUS_META[t.status];
              const tm=TREND_META[t.trend];
              const bad=t.refundRatio>=0.15;
              const open=expanded.has(t.name);
              const topAgents=[...t.agents].sort((a,b)=>b.progress-a.progress);
              return(<React.Fragment key={t.name}>
                <tr onClick={()=>toggleExpand(t.name)} style={{borderTop:`1px solid ${C.lineSoft}`,background:i%2?"#FffdF9":C.card,cursor:"pointer"}}>
                  <td style={{padding:"12px 14px"}}><div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{color:C.ink30,fontSize:11,width:12,display:"inline-block",transform:open?"rotate(90deg)":"none",transition:"transform .15s"}}>▶</span>
                    <div style={{width:34,height:34,borderRadius:10,background:sm.bg,color:sm.c,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14,flexShrink:0}}>{t.name[0]}</div>
                    <div><div style={{fontFamily:FONT_DISP,fontWeight:700,fontSize:14}}>Pod {t.name}</div><div style={{fontSize:11.5,color:C.ink50}}>{t.onTarget} of {t.count} on target</div></div>
                  </div></td>
                  <td style={{padding:"12px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13,fontWeight:500}}>{t.count}</td>
                  <td style={{padding:"12px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13,color:C.ink70}}>{money(t.target)}</td>
                  <td style={{padding:"12px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13}}>{money(t.gross)}</td>
                  <td style={{padding:"12px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13,fontWeight:600}}>{money(t.net)}</td>
                  <td style={{padding:"12px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13,color:C.red}}>{money(t.refunds)}</td>
                  <td style={{padding:"12px 14px",textAlign:"right",fontFamily:FONT_MONO,fontSize:13,color:bad?C.red:C.ink70,fontWeight:bad?600:400}}>{pct(t.refundRatio)}</td>
                  <td style={{padding:"12px 14px",width:190}}><ProgressBar value={t.progress} color={sm.c}/></td>
                  <td style={{padding:"12px 14px"}}><div style={{display:"flex",alignItems:"center",gap:8}}><Sparkline data={t.history} color={tm.c}/><span style={{fontSize:11.5,fontWeight:600,color:tm.c}}>{tm.label}</span></div></td>
                  <td style={{padding:"12px 14px"}}><Pill label={sm.label} color={sm.c} bg={sm.bg}/></td>
                </tr>
                {open&&(
                  <tr style={{background:"#FBF4EA"}}>
                    <td colSpan={10} style={{padding:"4px 14px 14px 46px"}}>
                      <div style={{fontSize:11.5,color:C.ink50,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,margin:"10px 0 8px"}}>Agents in Pod {t.name}</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:8}}>
                        {topAgents.map(a=>{const asm=STATUS_META[a.status];return(
                          <div key={a.id} style={{display:"flex",alignItems:"center",gap:10,background:C.card,border:`1px solid ${C.line}`,borderRadius:10,padding:"8px 10px"}}>
                            <span style={{width:7,height:7,borderRadius:99,background:asm.c,flexShrink:0}}/>
                            <div style={{minWidth:0,flex:"0 0 120px"}}><div style={{fontSize:12.5,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.name}</div><div style={{fontSize:11,color:C.ink50}}>{money(a.net)} net</div></div>
                            <div style={{flex:1,minWidth:60}}><ProgressBar value={a.progress} color={asm.c}/></div>
                          </div>
                        );})}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>);
            })}
          </tbody>
        </table>
      </div>
      <div style={{padding:"10px 16px",borderTop:`1px solid ${C.lineSoft}`,fontSize:12,color:C.ink50,display:"flex",justifyContent:"space-between"}}>
        <span>{teamsSorted.length} pods · {tSum.agents} agents</span>
        <span>Team target is the sum of its agents' targets · {period.label}</span>
      </div>
    </div>
  </>);
}
